export const draftShareString = ' - DraftShare';

/**
 * The version name for a possession-pass event. ONE vocabulary — CKEditor and
 * the docx-native path both call this, so history reads identically across the
 * migration boundary. Returns '' when no version should be saved.
 */
export const possessionPassVersionName = ({
  success,
  possessionTeamName,
  sentForExternalReview = false,
  isInternalReview = false,
}: {
  success: boolean;
  possessionTeamName?: string;
  sentForExternalReview?: boolean;
  isInternalReview?: boolean;
}): string => {
  if (!success) return '';
  if (isInternalReview) return 'Sent for Internal Review';
  if (sentForExternalReview) {
    return `Draft Prepared for Sharing by ${possessionTeamName}${draftShareString}`;
  }
  return `Version Shared by ${possessionTeamName}`;
};

export const saveDocumentVersionOnPossessionPass = async ({
  success,
  possessionTeamName,
  sentForExternalReview = false,
  isInternalReview = false,
  // Supplies the polymorphic saveDocumentVersion mutation. Callers that omit it
  // compute the NAME only (see possessionPassVersionName) and save nothing —
  // that is the failure path, where `success: false` yields an empty name.
  docx,
}: {
  success: boolean;
  possessionTeamName?: string;
  sentForExternalReview?: boolean;
  isInternalReview?: boolean;
  docx?: {
    documentId: string;
    saveVersion: (name: string, event: string) => Promise<unknown>;
  };
}) => {
  const revisionName = possessionPassVersionName({
    success,
    possessionTeamName,
    sentForExternalReview,
    isInternalReview,
  });
  if (!revisionName) return;

  // DOCX-native deal: persist through the mutation with the SAME name.
  if (docx) {
    try {
      return await docx.saveVersion(revisionName, 'possession_transfer');
    } catch (error) {
      console.error('saveDocumentVersionOnPossessionPass (docx): error creating version:', error);
      return;
    }
  }

  // No persister supplied: nothing to save. This was CKEditor's
  // RevisionTracker branch, unreachable since the editor was removed.
  console.warn('saveDocumentVersionOnPossessionPass: no version persister supplied; skipping.');
  return;
};
